<template>
    <div class="event-parameters">
        <div v-if="!eventType" class="empty-parameters">
            <i class="fa-solid fa-arrow-up"></i>
            {{ __('notification_management.select_event_first') }}
        </div>

        <div v-else-if="!isSupportedEvent" class="empty-parameters">
            <i class="fa-solid fa-triangle-exclamation"></i>
            {{ __('notification_management.event_not_supported_error') }}
        </div>

        <template v-else>
            <div v-if="isPoiEvent" class="parameter-field full-width">
                <label>{{ __('notification_management.pois_to_monitor') }}</label>
                <v-select :value="parameters.poi_ids || []" class="parameter-select" multiple :options="poiOptions"
                    :reduce="(poi) => poi.value" :close-on-select="false"
                    :placeholder="__('notification_management.select_pois')"
                    @input="updateParameter('poi_ids', $event)" />
            </div>

            <div v-if="isPoiEvent" class="parameter-field full-width">
                <label>{{ __('notification_management.poi_categories_to_monitor') }}</label>
                <v-select :value="parameters.poi_categories || []" class="parameter-select" multiple
                    :options="poiCategoryOptions" :reduce="(category) => category.value" :close-on-select="false"
                    :placeholder="__('notification_management.select_poi_categories')"
                    @input="updateParameter('poi_categories', $event)" />
            </div>

            <div v-if="isStoppedEvent" class="parameter-field">
                <label for="stopped-after-hours">
                    {{ __('notification_management.stopped_after_hours') }}
                    <span class="required-marker">*</span>
                </label>
                <duration-suggest-input input-id="stopped-after-hours"
                    :value="hoursToMinutes(parameters.duration_hours)" :min-minutes="60" preferred-unit="hours"
                    :invalid="!!errors.duration_hours" @input="updateDuration('duration_hours', $event, 'hours')" />
                <small v-if="errors.duration_hours" class="field-error">
                    {{ errors.duration_hours }}
                </small>
            </div>

            <div v-if="isStoppedEvent" class="parameter-field full-width">
                <label>{{ __('notification_management.excluded_stop_pois') }}</label>
                <v-select :value="parameters.exclude_pois || []" class="parameter-select" multiple :options="poiOptions"
                    :reduce="(poi) => poi.value" :close-on-select="false"
                    :placeholder="__('notification_management.select_pois')"
                    @input="updateParameter('exclude_pois', $event)" />
            </div>

            <div v-if="isStoppedEvent" class="parameter-field full-width">
                <label>{{ __('notification_management.excluded_stop_poi_categories') }}</label>
                <v-select :value="parameters.exclude_poi_categories || []" class="parameter-select" multiple
                    :options="poiCategoryOptions" :reduce="(category) => category.value" :close-on-select="false"
                    :placeholder="__('notification_management.select_poi_categories')"
                    @input="updateParameter('exclude_poi_categories', $event)" />
            </div>

            <div v-if="isStoppedEvent" class="parameter-field">
                <label for="stationary-cooldown-hours">
                    {{ __('notification_management.minimum_notification_interval') }}
                </label>
                <duration-suggest-input input-id="stationary-cooldown-hours"
                    :value="hoursToMinutes(parameters.cooldown_hours)" :min-minutes="60" preferred-unit="hours"
                    :invalid="!!errors.cooldown_hours" @input="updateDuration('cooldown_hours', $event, 'hours')" />
                <small v-if="errors.cooldown_hours" class="field-error">
                    {{ errors.cooldown_hours }}
                </small>
            </div>

            <div v-if="isStoppedEvent" class="parameter-field">
                <label for="stationary-notify-for-hours">
                    {{ __('notification_management.notify_for_hours') }}
                </label>
                <duration-suggest-input input-id="stationary-notify-for-hours"
                    :value="hoursToMinutes(parameters.notify_for_hours)" :min-minutes="60" preferred-unit="hours"
                    :invalid="!!errors.notify_for_hours" @input="updateDuration('notify_for_hours', $event, 'hours')" />
                <small v-if="errors.notify_for_hours" class="field-error">
                    {{ errors.notify_for_hours }}
                </small>
            </div>

            <div v-if="!isStoppedEvent" class="parameter-field">
                <label for="minimum-notification-interval">
                    {{ __('notification_management.minimum_notification_interval') }}
                </label>
                <duration-suggest-input input-id="minimum-notification-interval" :value="parameters.cooldown_minutes"
                    :min-minutes="3" preferred-unit="minutes" :invalid="!!errors.cooldown_minutes"
                    @input="updateDuration('cooldown_minutes', $event, 'minutes')" />
                <small v-if="errors.cooldown_minutes" class="field-error">
                    {{ errors.cooldown_minutes }}
                </small>
            </div>
        </template>
    </div>
</template>

<script>
    import DurationSuggestInput from '@/components/commons/DurationSuggestInput.vue'

    export default {
        name: 'NotificationEventParameters',
        components: {
            DurationSuggestInput
        },
        props: {
            eventType: {
                type: String,
                default: null
            },
            parameters: {
                type: Object,
                required: true
            },
            pois: {
                type: Array,
                default: () => []
            },
            poiCategories: {
                type: Array,
                default: () => []
            }
        },
        data() {
            return {
                errors: {}
            }
        },
        computed: {
            isPoiEvent() {
                return ['poi_in', 'poi_out'].includes(this.eventType)
            },
            isStoppedEvent() {
                return this.eventType === 'vehicle_stationary'
            },
            isSupportedEvent() {
                return [
                    'poi_in',
                    'poi_out',
                    'vehicle_stationary',
                    'low_temperature',
                    'high_temperature',
                    'door_open',
                    'door_closed'
                ].includes(this.eventType)
            },
            poiOptions() {
                return this.pois
                    .filter((poi) => poi?.id != null && poi?.description)
                    .map((poi) => ({ value: poi.id, label: poi.description }))
                    .sort((a, b) => a.label.localeCompare(b.label))
            },
            poiCategoryOptions() {
                return this.poiCategories
                    .filter((category) => category?.id != null && category?.description)
                    .map((category) => ({ value: category.id, label: category.description }))
                    .sort((a, b) => a.label.localeCompare(b.label))
            }
        },
        methods: {
            updateParameter(key, value) {
                this.$emit('input', { ...this.parameters, [key]: value })
                this.$delete(this.errors, key)
            },
            hoursToMinutes(value) {
                return value === null || value === '' ? null : Number(value) * 60
            },
            updateDuration(key, minutes, apiUnit) {
                const value =
                    minutes === null || minutes === ''
                        ? null
                        : apiUnit === 'hours'
                            ? Number(minutes) / 60
                            : Number(minutes)
                this.updateParameter(key, value)
            },
            validate() {
                const errors = {}
                if (
                    this.isStoppedEvent &&
                    !(Number(this.parameters.duration_hours) > 0)
                ) {
                    errors.duration_hours = this.__('notification_management.required_positive_number')
                }

                const nonNegativeFields = this.isStoppedEvent
                    ? ['cooldown_hours', 'notify_for_hours']
                    : ['cooldown_minutes']
                nonNegativeFields.forEach((field) => {
                    if (this.parameters[field] != null && Number(this.parameters[field]) < 0) {
                        errors[field] = this.__('notification_management.non_negative_number')
                    }
                })

                this.errors = errors
                return Object.keys(errors).length === 0
            }
        }
    }
</script>

<style scoped lang="scss">
    .event-parameters {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
    }

    .empty-parameters {
        display: flex;
        grid-column: 1 / -1;
        align-items: center;
        gap: 9px;
        border: 1px dashed #b7c0ca;
        border-radius: 12px;
        padding: 16px;
        background: #fff;
        color: #6b7785;
        font-size: 12px;
    }

    .parameter-field {
        min-width: 0;

        &.full-width {
            grid-column: 1 / -1;
        }

        label {
            display: block;
            margin-bottom: 7px;
            font-family: 'Stolzl-Medium';
            font-size: 12px;
        }
    }

    .required-marker,
    .field-error {
        color: var(--danger);
    }

    .field-error {
        display: block;
        margin-top: 6px;
        font-size: 10px;
    }

    .parameter-select {
        font-size: 13px;

        ::v-deep(.vs__dropdown-toggle) {
            min-height: 42px;
            border: 1px solid #d2d2d2;
            border-radius: 12px;
            background: #fff;
        }
    }

    @media (max-width: 760px) {
        .event-parameters {
            grid-template-columns: 1fr;
        }

        .parameter-field.full-width {
            grid-column: auto;
        }
    }
</style>
